/** @jsxImportSource @emotion/react */
'use memo'

// web/src/features/event/components/EventDateTimeField.tsx
import css from '@emotion/css/macro'
import { Global } from '@emotion/react'
import { CalendarOutlined, ClockCircleOutlined } from '@ant-design/icons'
import { DatePicker as AntDatePicker, TimePicker as AntTimePicker } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { useField, useFormikContext } from 'formik'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import {
  registrationDateTimeFromUtcIso,
  registrationDateTimeToUtcIso,
} from '@sportninja/common/ts-utils/registration-datetime'
import type { EventFormValues } from '@sportninja/common/features/event/types'
import {
  orderEventWindow,
  type EventWindowBoundary,
} from '@sportninja/common/features/event/orderEventWindow'
import { paragraphSmall, weightRegular } from 'src/components/css'
import Text from 'src/components/ts-components/Text'
import { ErrorLabel } from 'src/components/ts-components/ErrorLabel'

/**
 * Takes the calendar/clock panel OUT of the modal's two clipping boxes.
 *
 * The panel is appended next to its own field (see `popupContainer`), which
 * sits inside `ScrollContainer` (`overflow-y: auto`, capped at 60vh) inside
 * `.ant-modal-body` (`overflow-y: auto`). Both clip it, and both
 * shrink the box rc-align measures for `autoAdjustOverflow` — so a field in the
 * lower half of the form "had no room below", flipped its panel upward, and the
 * top of it was then cut off by the modal header. Fixed positioning solves both
 * halves at once: it escapes ancestor overflow clipping, AND makes rc-align
 * measure against the VIEWPORT, so the panel opens downward like it should.
 *
 * Same pairing the shared `ts-components/DatePicker` uses, for the same reason.
 * Module scope so the object identity is stable across renders.
 */
const POPUP_STYLE: React.CSSProperties = { position: 'fixed' }

interface EventDateTimeFieldProps {
  /** Formik field holding a UTC ISO string. */
  name: string
  /**
   * Label PREFIX, e.g. 'Start' — composed into 'Start Date' and
   * 'Start Time (PDT)' for the two controls. Pass the bare noun, not a full
   * label, since each half appends its own half's name.
   */
  label: string
  /**
   * The authoring admin's own account timezone — the wall-clock picked here is
   * interpreted in it. Omitted falls back to the browser's zone.
   */
  accountTimezone?: string
  /** Zone abbreviation for the label, e.g. 'PDT'. */
  zoneAbbr: string
  disabled?: boolean
  /** Appends a `*` to BOTH the date and time labels. */
  required?: boolean
  /**
   * Which end of the event's window this field is, when it participates in the
   * start/end ordering rule. Omitted for a field with no counterpart (Repeat
   * Until), which is then committed without any ordering check.
   */
  boundary?: EventWindowBoundary
}

// `type: 'mask'` gives a guided input — the placeholder stays in place and the
// caret steps through the segments as the admin types, so a partially entered
// value is not rejected the way a plain format string would. Same as
// RegistrationSetup's window fields.
// `.tz()` below needs these. They are idempotent, and registering here rather
// than relying on another module's import side effect keeps this file standalone.
dayjs.extend(utc)
dayjs.extend(timezone)

const DATE_MASK = { format: 'MM/DD/YYYY', type: 'mask' } as const

// Deliberately NOT masked. rc-picker's MaskFormat only treats
// ['YYYY','MM','DD','HH','mm','ss','SSS'] as editable cells, so under a mask a
// 12-hour `hh:mm A` exposes only the minutes — the hours and the AM/PM marker
// cannot be typed. A plain format keeps the field free-text.
const TIME_FORMAT = 'hh:mm A'

/** Quarter-hour steps: events are scheduled on the clock, not to the minute.
 *  The field is free-text, so any other minute can still be TYPED — the step
 *  only controls the dropdown's listed options. */
const MINUTE_STEP = 15

const TIME_POPUP_CLASS = 'event-datetime-time-popup'

/**
 * antd renders the time columns in a portal, so the selected cell is styled
 * through a popup class rather than the trigger's css prop.
 *
 * DEFAULT_FLAIR is the token the theme feeds antd as `colorPrimary`, so the
 * selected time cell matches the selected day in the calendar.
 */
const timePopupStyles = css`
  .${TIME_POPUP_CLASS}
    .ant-picker-time-panel-column
    > li.ant-picker-time-panel-cell-selected
    .ant-picker-time-panel-cell-inner {
    background: ${colors.DEFAULT_FLAIR};
    color: ${colors.WHITE};

    &:hover {
      background: ${colors.DEFAULT_FLAIR};
      color: ${colors.WHITE};
    }
  }

  /* antd's built-in Now link SNAPS to the nearest minuteStep — at 11:22 with
     15-minute steps it commits 11:15. It is replaced by the footer button
     below, which commits the true current minute. */
  .${TIME_POPUP_CLASS} .ant-picker-ranges .ant-picker-now {
    display: none;
  }
`

/* The button is centered by STRETCHING it across antd's footer cell, which is
   otherwise left-aligned — text-align on the button alone would not move it,
   since a button shrink-wraps its label. */
const nowButtonStyle = css`
  ${paragraphSmall}
  ${weightRegular}
  display: block;
  width: 100%;
  background: none;
  border: none;
  padding: 8px 0;
  cursor: pointer;
  text-align: center;
  color: ${colors.WHITE};

  &:hover {
    opacity: 0.8;
  }
`

/**
 * One instant, authored as SEPARATE date and time controls.
 *
 * Replaces the single `showTime` picker: that one commits date and time
 * together behind an OK button, so changing only the time meant re-confirming
 * the date. Split fields each own their half and commit on their own —
 * matching RegistrationSetup's General tab.
 *
 * Both halves read and write ONE Formik value (a UTC ISO string). Authoring
 * happens in the ADMIN'S ACCOUNT timezone: the wall-clock typed here is
 * interpreted as account-local and converted to UTC on the way out, so an
 * event created for "6 PM" by an admin in Toronto reads as 3 PM in Vancouver.
 */
const EventDateTimeField: React.FC<EventDateTimeFieldProps> = ({
  name,
  label,
  accountTimezone,
  zoneAbbr,
  disabled,
  required = false,
  boundary,
}) => {
  const [field, meta, helpers] = useField<string>(name)
  const { values, setFieldValue, setFieldError } =
    useFormikContext<EventFormValues>()

  const hasError = Boolean(meta.touched && meta.error)
  const errorProps = hasError ? { [name]: meta.error as string } : {}

  // The stored UTC instant, expressed in the authoring zone for display.
  const value: Dayjs | null = field.value
    ? accountTimezone
      ? registrationDateTimeFromUtcIso(field.value, accountTimezone)
      : dayjs(field.value)
    : null

  const commit = (next: Dayjs | null) => {
    const isValid = next && next.isValid()
    const iso = isValid
      ? accountTimezone
        ? registrationDateTimeToUtcIso(next, accountTimezone)
        : next.toISOString()
      : ''

    // Without a boundary this field has no counterpart to order against
    // (Repeat Until), so it just writes its own value.
    if (!boundary) {
      helpers.setValue(iso)
      if (meta.error) {
        setFieldError(name, undefined)
      }
      return
    }

    // Picking a start after the end (or an end before the start) clears the
    // OTHER end rather than rejecting the click — the user's newest choice
    // always wins. See orderEventWindow.
    const ordered = orderEventWindow(
      {
        startsAt: boundary === 'start' ? iso : values.starts_at,
        endsAt: boundary === 'end' ? iso : values.ends_at,
      },
      boundary
    )

    setFieldValue('starts_at', ordered.startsAt)
    setFieldValue('ends_at', ordered.endsAt)

    if (meta.error) {
      setFieldError(name, undefined)
    }
  }

  /**
   * A date with no time yet is seeded with the CURRENT time rather than
   * midnight — picking a day and getting 12:00 AM meant every event had to
   * have its time corrected by hand.
   *
   * "Now" is read in the AUTHORING zone, not the browser's: the wall-clock
   * committed here is interpreted in `accountTimezone`, so seeding from the
   * device clock would silently shift the time for an admin working outside
   * their account's zone.
   *
   * Seeded to the exact current minute, unrounded — the admin adjusts it if
   * they need something else. The dropdown lists quarter-hours, but the field
   * is free-text, so an off-step seed like 11:20 is still a valid, editable
   * value.
   *
   * Choosing a new date for an existing value KEEPS its time — only the day
   * moves.
   */
  const handleDateChange = (next: Dayjs | null) => {
    if (!next || !next.isValid()) {
      commit(null)
      return
    }

    const now = accountTimezone ? dayjs().tz(accountTimezone) : dayjs()
    const base = value ?? next.hour(now.hour()).minute(now.minute())

    commit(
      next.hour(base.hour()).minute(base.minute()).second(0).millisecond(0)
    )
  }

  /**
   * Applies the picked time to the already-chosen DAY.
   *
   * **Typing a time and picking one from the panel do not produce the same
   * value.** The panel applies the selection onto the current value, so the day
   * rides along. TYPING is parsed against `TIME_FORMAT` ('hh:mm A'), which
   * carries no date tokens, so dayjs fills the date in from TODAY. Committing
   * that verbatim silently moved a September event to whatever today is — and
   * only when the admin typed, which is what made it look like a picker quirk
   * rather than a data bug.
   *
   * So the day is taken from `value` and only the clock from `next`, mirroring
   * `handleNow`: this control owns the time half, never the day. Its sibling
   * `handleDateChange` already does the reverse, which is why typing a DATE was
   * never affected.
   */
  const handleTimeChange = (next: Dayjs | null) => {
    if (!next || !next.isValid()) {
      commit(null)
      return
    }

    // No day chosen yet means nothing to preserve. Not reachable through the
    // UI — the picker is disabled until the date is set — but committing
    // `next` unchanged is the right answer if it ever is.
    if (!value) {
      commit(next)
      return
    }

    // Seconds zeroed to match every other commit here; the format offers no
    // seconds, so a parsed value carries none worth keeping.
    commit(
      value.hour(next.hour()).minute(next.minute()).second(0).millisecond(0)
    )
  }

  /**
   * Commits the exact current wall-clock in the AUTHORING zone, seconds
   * zeroed. Replaces antd's own "Now", which rounds to `minuteStep` and so
   * lands up to 14 minutes away from the real time.
   *
   * Keeps the already-chosen DAY — this control owns the time half only.
   */
  const handleNow = () => {
    if (!value) {
      return
    }

    const now = accountTimezone ? dayjs().tz(accountTimezone) : dayjs()

    commit(value.hour(now.hour()).minute(now.minute()).second(0).millisecond(0))
  }

  // Anchored to the field so the panel travels with the modal instead of
  // staying put when the page behind it scrolls.
  const popupContainer = (triggerNode: HTMLElement) =>
    triggerNode.parentNode as HTMLElement

  return (
    <div css={fieldCellStyle}>
      <Global styles={timePopupStyles} />

      <div css={rowStyle}>
        <div css={halfStyle}>
          <Text variant='paragraphSmall' weight='regular' color={colors.WHITE}>
            {capitalizeWords(`${label} Date`)}
            {required && <span css={requiredMarkStyle}>*</span>}
          </Text>
          <AntDatePicker
            value={value}
            onChange={handleDateChange}
            format={DATE_MASK}
            status={hasError ? 'error' : ''}
            disabled={disabled}
            placeholder={capitalizeWords('Select date')}
            css={controlStyle}
            suffixIcon={<CalendarOutlined css={iconStyle} />}
            getPopupContainer={popupContainer}
            popupStyle={POPUP_STYLE}
          />
        </div>

        <div css={halfStyle}>
          {/* The zone rides on the TIME label only: it qualifies the clock,
              not the calendar day. */}
          <Text variant='paragraphSmall' weight='regular' color={colors.WHITE}>
            {`${capitalizeWords(`${label} Time`)} (${zoneAbbr})`}
            {required && <span css={requiredMarkStyle}>*</span>}
          </Text>
          <AntTimePicker
            value={value}
            onChange={handleTimeChange}
            format={TIME_FORMAT}
            use12Hours
            minuteStep={MINUTE_STEP}
            status={hasError ? 'error' : ''}
            // A time cannot be stored without a day, so stay disabled until the
            // date is chosen rather than silently attaching it to today.
            disabled={disabled || !value}
            needConfirm={false}
            placeholder={capitalizeWords('Select time')}
            css={controlStyle}
            suffixIcon={<ClockCircleOutlined css={iconStyle} />}
            popupClassName={TIME_POPUP_CLASS}
            getPopupContainer={popupContainer}
            popupStyle={POPUP_STYLE}
            renderExtraFooter={() => (
              <button type='button' onClick={handleNow} css={nowButtonStyle}>
                {capitalizeWords('Now')}
              </button>
            )}
          />
        </div>
      </div>

      {/* Record shape, not the array shape: ErrorLabel Title-Cases array-shaped
          errors (legacy static copy) and would mangle the API's own wording. */}
      <ErrorLabel
        fieldErrors={errorProps}
        customError={errorProps}
        name={name}
      />
    </div>
  )
}

export default EventDateTimeField

// ---- styled ----
const fieldCellStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`

const rowStyle = css`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;

  /* The pair sits inside a half-width modal column already; below this the two
     controls would each be too narrow to read, so they stack. */
  @container (max-width: 320px) {
    flex-direction: column;
  }
`

/* A column so the sub-label sits above its control.

   8px, matching the shared `FieldCell` every other field in the event form
   uses. It was 4px (copied from RegistrationSetup's window fields), which read
   fine while these pickers had a row to themselves — but once Event Length and
   Arrival Time joined them, the 4px labels sat visibly higher than the 8px one
   beside them in the same row. */
const halfStyle = css`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  flex: 1 0 0;
  min-width: 0;
`

/* Matches the shared DatePicker chrome so these sit in the form exactly like
   the single picker they replace. */
const controlStyle = css`
  ${paragraphSmall}
  ${weightRegular}
  height: 40px !important;
  width: 100%;
  border-radius: 8px;
  border: 1px solid ${colors.SECONDARY_100};
  background: ${colors.SECONDARY_200};
`

const iconStyle = css`
  color: ${colors.WHITE};
  font-size: 18px;
`

const requiredMarkStyle = css`
  margin-left: 4px;
`
